/**
 * The client's copy of the room's log.
 *
 * Every `view` message carries a tail of the server's log and the index it
 * starts at: the causing command's events in the usual case, the whole log with
 * `from: 0` when a connection is (re)joining. Splicing at `from` is therefore
 * the one operation the client needs — it appends the usual case, and replaces
 * the lot on a rejoin — and it is idempotent, so a message delivered twice
 * leaves the log alone rather than doubling the last few lines.
 */
import type { GameEvent } from '@trajan/engine';

export interface SplicedLog {
  log: GameEvent[];
  /**
   * How many entries the client never saw. A live connection delivers messages
   * in order and a rejoin is sent the whole log, so this is zero; it exists so
   * that reconnection (Slice 3) can notice a hole and ask for the log again
   * rather than quietly showing an incomplete one.
   */
  missed: number;
}

export function spliceLog(
  log: readonly GameEvent[],
  from: number,
  events: readonly GameEvent[],
): SplicedLog {
  const at = Math.max(0, Math.min(from, log.length));
  return { log: [...log.slice(0, at), ...events], missed: Math.max(0, from - log.length) };
}
